import { useState } from 'react'
import { Checkbox, Empty, Spin, Tag } from 'antd'
import { ProjectMaterialPoolDTO } from '@/api/projectPoolApi'
import { PLATFORM_STATUS_STYLES, moneyOf, textOf } from '../PoolTable'
import Style from '../../style.module.scss'

/** 图片：无图或加载失败时显示“暂无图片”占位（PRD DET-001 3.3）；卡片与详情抽屉共用 */
export const PoolImage = ({ url }: { url: string | null }) => {
  const [failed, setFailed] = useState(false)
  if (!url || failed) {
    return <div className={Style.noImage}>暂无图片</div>
  }
  return <img src={url} alt="" onError={() => setFailed(true)} />
}

/**
 * 平台状态标签 + 厂控 / 实供圆点：圆点仅在字段已授权且值为“是”时展示，默认不显示文字，悬停或聚焦时显示（PRD LIST-001 3.3、DET-001 BR-09）。
 * 卡片与详情抽屉共用。
 */
export const PlatformStatus = ({
  item,
  fields,
  withLabel = true,
}: {
  item: ProjectMaterialPoolDTO
  fields: string[]
  withLabel?: boolean // 标签内是否带“平台状态：”前缀；详情已有字段名时不带
}) => (
  <div className={Style.cardStatus}>
    <Tag
      className={Style.statusTag}
      color={PLATFORM_STATUS_STYLES[item.platformStatus || '']?.tag}
    >
      {withLabel && '平台状态：'}
      {textOf(item.platformStatusName)}
    </Tag>
    {fields.includes('factoryControl') && item.factoryControl === true && (
      <span
        className={Style.flagFactory}
        tabIndex={0}
        aria-label="厂控物料：是"
      >
        控
      </span>
    )}
    {fields.includes('actualSupply') && item.actualSupply === '1' && (
      <span className={Style.flagActual} tabIndex={0} aria-label="实供物料：是">
        实
      </span>
    )}
  </div>
)

interface Props {
  list: ProjectMaterialPoolDTO[]
  fields: string[] // 当前角色可见字段 code，厂控、实供图标跟随字段权限
  loading: boolean
  selectedIds: string[]
  onToggle: (id: string, checked: boolean) => void
  onSelect: (ids: string[]) => void
  onOpenDetail: (xhCode: string) => void
}

/**
 * 项目商品池 - 商品卡片（按原型）：主图、客户标题（为空时显示咸亨标题，用户确认）、平台状态、厂控/实供图标及必显摘要字段。
 * 依据 PRD LIST-001 3.3。
 */
const PoolCards = (props: Props) => {
  const { list, fields, loading, selectedIds, onToggle, onSelect } = props
  const { onOpenDetail } = props
  const allChecked =
    list.length > 0 && list.every((item) => selectedIds.includes(item.id))

  return (
    <Spin spinning={loading} wrapperClassName={Style.cardsSpin}>
      <div className={Style.cardsHeader}>
        <Checkbox
          checked={allChecked}
          indeterminate={!allChecked && selectedIds.length > 0}
          disabled={!list.length}
          onChange={(e) =>
            onSelect(e.target.checked ? list.map((item) => item.id) : [])
          }
        >
          全选当前页
        </Checkbox>
      </div>
      <div className={Style.cardsBody}>
        {!list.length && !loading && <Empty className={Style.listEmpty} />}
        <div className={Style.cardGrid}>
          {list.map((item) => {
            const title = item.customerTitle || item.xhTitle || ''
            return (
              // 点击卡片打开详情；勾选框只改变勾选，不打开详情（PRD LIST-001）
              <div
                key={item.id}
                className={Style.card}
                role="button"
                tabIndex={0}
                onClick={() => onOpenDetail(item.xhCode)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onOpenDetail(item.xhCode)
                }}
              >
                <Checkbox
                  className={Style.cardCheck}
                  checked={selectedIds.includes(item.id)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => onToggle(item.id, e.target.checked)}
                />
                <div className={Style.cardImage}>
                  <PoolImage url={item.mainImageUrl} />
                </div>
                <div className={Style.cardTitle} title={title}>
                  {textOf(title)}
                </div>
                <PlatformStatus item={item} fields={fields} />
                <div className={Style.cardInfo}>
                  <div className={Style.infoRowFull}>
                    <span className={Style.infoLabel}>咸亨标题</span>
                    <span
                      className={Style.infoValue}
                      title={item.xhTitle || ''}
                    >
                      {textOf(item.xhTitle)}
                    </span>
                  </div>
                  <div className={Style.infoRow}>
                    <span className={Style.infoLabel}>咸亨编码</span>
                    <span className={Style.infoValue} title={item.xhCode}>
                      {item.xhCode}
                    </span>
                  </div>
                  <div className={Style.infoRow}>
                    <span className={Style.infoLabel}>客户编码</span>
                    <span className={Style.infoValue}>
                      {textOf(item.customerProductCode)}
                    </span>
                  </div>
                  <div className={Style.infoRow}>
                    <span className={Style.infoLabel}>项目售价</span>
                    <span className={Style.price}>
                      {item.projectPrice === null
                        ? textOf(null)
                        : `¥${moneyOf(item.projectPrice)}`}
                    </span>
                  </div>
                  <div className={Style.infoRow}>
                    <span className={Style.infoLabel}>品牌名称</span>
                    <span
                      className={Style.infoValue}
                      title={item.brandName || ''}
                    >
                      {textOf(item.brandName)}
                    </span>
                  </div>
                  <div className={Style.infoRow}>
                    <span className={Style.infoLabel}>类目名称</span>
                    <span
                      className={Style.infoValue}
                      title={item.customerCategoryName || ''}
                    >
                      {textOf(item.customerCategoryName)}
                    </span>
                  </div>
                  <div className={Style.infoRow}>
                    <span className={Style.infoLabel}>单位名称</span>
                    <span className={Style.infoValue}>
                      {textOf(item.unitName)}
                    </span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </Spin>
  )
}

export default PoolCards
import { useState } from 'react'
import { Checkbox, Empty, Spin, Tag } from 'antd'
import { ProjectMaterialPoolDTO } from '@/api/projectPoolApi'
import { PLATFORM_STATUS_STYLES, moneyOf, textOf } from '../PoolTable'
import Style from '../../style.module.scss'

/** 图片：无图或加载失败时显示“暂无图片”占位（PRD DET-001 3.3）；卡片与详情抽屉共用 */
export const PoolImage = ({ url }: { url: string | null }) => {
  const [failed, setFailed] = useState(false)
  if (!url || failed) {
    return <div className={Style.noImage}>暂无图片</div>
  }
  return <img src={url} alt="" onError={() => setFailed(true)} />
}

/**
 * 平台状态标签 + 厂控 / 实供圆点：圆点仅在字段已授权且值为“是”时展示，默认不显示文字，悬停或聚焦时显示（PRD LIST-001 3.3、DET-001 BR-09）。
 * 卡片与详情抽屉共用。
 */
export const PlatformStatus = ({
  item,
  fields,
  withLabel = true,
}: {
  item: ProjectMaterialPoolDTO
  fields: string[]
  withLabel?: boolean // 标签内是否带“平台状态：”前缀；详情已有字段名时不带
}) => (
  <div className={Style.cardStatus}>
    <Tag
      className={Style.statusTag}
      color={PLATFORM_STATUS_STYLES[item.platformStatus || '']?.tag}
    >
      {withLabel && '平台状态：'}
      {textOf(item.platformStatusName)}
    </Tag>
    {fields.includes('factoryControl') && item.factoryControl === true && (
      <span
        className={Style.flagFactory}
        tabIndex={0}
        aria-label="厂控物料：是"
      >
        控
      </span>
    )}
    {fields.includes('actualSupply') && item.actualSupply === '1' && (
      <span className={Style.flagActual} tabIndex={0} aria-label="实供物料：是">
        实
      </span>
    )}
  </div>
)

interface Props {
  list: ProjectMaterialPoolDTO[]
  fields: string[] // 当前角色可见字段 code，厂控、实供图标跟随字段权限
  loading: boolean
  selectedIds: string[]
  onToggle: (id: string, checked: boolean) => void
  onSelect: (ids: string[]) => void
  onOpenDetail: (xhCode: string) => void
}

/**
 * 项目商品池 - 商品卡片（按原型）：主图、客户标题（为空时显示咸亨标题，用户确认）、平台状态、厂控/实供图标及必显摘要字段。
 * 依据 PRD LIST-001 3.3。
 */
const PoolCards = (props: Props) => {
  const { list, fields, loading, selectedIds, onToggle, onSelect } = props
  const { onOpenDetail } = props
  const allChecked =
    list.length > 0 && list.every((item) => selectedIds.includes(item.id))

  return (
    <Spin spinning={loading} wrapperClassName={Style.cardsSpin}>
      <div className={Style.cardsHeader}>
        <Checkbox
          checked={allChecked}
          indeterminate={!allChecked && selectedIds.length > 0}
          disabled={!list.length}
          onChange={(e) =>
            onSelect(e.target.checked ? list.map((item) => item.id) : [])
          }
        >
          全选当前页
        </Checkbox>
      </div>
      <div className={Style.cardsBody}>
        {!list.length && !loading && <Empty className={Style.listEmpty} />}
        <div className={Style.cardGrid}>
          {list.map((item) => {
            const title = item.customerTitle || item.xhTitle || ''
            return (
              // 点击卡片打开详情；勾选框只改变勾选，不打开详情（PRD LIST-001）
              <div
                key={item.id}
                className={Style.card}
                role="button"
                tabIndex={0}
                onClick={() => onOpenDetail(item.xhCode)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onOpenDetail(item.xhCode)
                }}
              >
                <Checkbox
                  className={Style.cardCheck}
                  checked={selectedIds.includes(item.id)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => onToggle(item.id, e.target.checked)}
                />
                <div className={Style.cardImage}>
                  <PoolImage url={item.mainImageUrl} />
                </div>
                <div className={Style.cardTitle} title={title}>
                  {textOf(title)}
                </div>
                <PlatformStatus item={item} fields={fields} />
                <div className={Style.cardInfo}>
                  <div className={Style.infoRowFull}>
                    <span className={Style.infoLabel}>咸亨标题</span>
                    <span
                      className={Style.infoValue}
                      title={item.xhTitle || ''}
                    >
                      {textOf(item.xhTitle)}
                    </span>
                  </div>
                  <div className={Style.infoRow}>
                    <span className={Style.infoLabel}>咸亨编码</span>
                    <span className={Style.infoValue} title={item.xhCode}>
                      {item.xhCode}
                    </span>
                  </div>
                  <div className={Style.infoRow}>
                    <span className={Style.infoLabel}>客户编码</span>
                    <span className={Style.infoValue}>
                      {textOf(item.customerProductCode)}
                    </span>
                  </div>
                  <div className={Style.infoRow}>
                    <span className={Style.infoLabel}>项目售价</span>
                    <span className={Style.price}>
                      {item.projectPrice === null
                        ? textOf(null)
                        : `¥${moneyOf(item.projectPrice)}`}
                    </span>
                  </div>
                  <div className={Style.infoRow}>
                    <span className={Style.infoLabel}>品牌名称</span>
                    <span
                      className={Style.infoValue}
                      title={item.brandName || ''}
                    >
                      {textOf(item.brandName)}
                    </span>
                  </div>
                  <div className={Style.infoRow}>
                    <span className={Style.infoLabel}>类目名称</span>
                    <span
                      className={Style.infoValue}
                      title={item.customerCategoryName || ''}
                    >
                      {textOf(item.customerCategoryName)}
                    </span>
                  </div>
                  <div className={Style.infoRow}>
                    <span className={Style.infoLabel}>单位名称</span>
                    <span className={Style.infoValue}>
                      {textOf(item.unitName)}
                    </span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </Spin>
  )
}

export default PoolCards
